import { gql } from '@apollo/client';

import type { Grant, GrantCondition, PermissionLevel } from '@/lib/brokerage-permissions';

/**
 * Brokerages as staff see them: the ones in your portfolio (`me
 * { managedTenants }`), each brokerage's members and roles, and the portfolio
 * writes an Admin makes. Every brokerage-scoped call names its `tenantId`.
 */

export type MemberRole = 'ADVISOR' | 'ADMIN' | 'OWNER' | 'PENDING' | 'AUDIT' | 'SYSTEM' | 'STAFF';

export interface BrokerageSummary {
  id: string;
  name: string | null;
  slug: string | null;
  companyName: string | null;
}

const SUMMARY_FIELDS = `
  id
  name
  slug
  companyName
`;

export const MY_BROKERAGES_QUERY = gql`
  query MyBrokerages {
    me {
      id
      managedTenants {
        ${SUMMARY_FIELDS}
      }
    }
  }
`;

export interface MyBrokeragesResult {
  me: { id: string; managedTenants: BrokerageSummary[] };
}

export interface RoleGrant extends Grant {
  target?: string | null;
  condition: GrantCondition;
}

export interface BrokerageRole {
  id: string;
  name: string | null;
  presetKey: string | null;
  source: 'ASSIGNMENT' | 'CLIENT' | null;
  grants: RoleGrant[];
  assigneeIds: string[];
}

export interface BrokerageMember {
  id: string;
  personId: string | null;
  role: string | null;
  createdAt: string | null;
  deletedAt: string | null;
  person: { id: string; extAppApiId: string | null; extTurnstileId: string | null } | null;
}

const ROLE_FIELDS = `
  id
  name
  presetKey
  source
  grants { resource level condition }
  assigneeIds
`;

const MEMBER_FIELDS = `
  id
  personId
  role
  createdAt
  deletedAt
  person { id extAppApiId extTurnstileId }
`;

export const BROKERAGE_QUERY = gql`
  query Brokerage($tenantId: UUID!) {
    brokerage(id: $tenantId) {
      ${SUMMARY_FIELDS}
    }
  }
`;

export interface BrokerageResult {
  brokerage: BrokerageSummary;
}

export const BROKERAGE_MEMBERS_QUERY = gql`
  query BrokerageMembers($tenantId: UUID!) {
    brokerage(id: $tenantId) {
      id
      members {
        ${MEMBER_FIELDS}
      }
    }
  }
`;

export interface BrokerageMembersResult {
  brokerage: { id: string; members: BrokerageMember[] };
}

export const BROKERAGE_ROLES_QUERY = gql`
  query BrokerageRoles($tenantId: UUID!) {
    roles(tenantId: $tenantId) {
      ${ROLE_FIELDS}
    }
  }
`;

export interface BrokerageRolesResult {
  roles: BrokerageRole[];
}

export const SET_MEMBER_ROLE = gql`
  mutation SetMemberRole($tenantId: UUID!, $personId: UUID!, $role: TenantMembershipRole!) {
    setMemberRole(tenantId: $tenantId, input: { personId: $personId }, role: $role) {
      id
      personId
      role
    }
  }
`;

export const DEACTIVATE_MEMBER = gql`
  mutation DeactivateMember($tenantId: UUID!, $personId: UUID!) {
    deactivateMember(tenantId: $tenantId, input: { personId: $personId }) {
      id
      deletedAt
    }
  }
`;

export const REACTIVATE_MEMBER = gql`
  mutation ReactivateMember($tenantId: UUID!, $personId: UUID!) {
    reactivateMember(tenantId: $tenantId, input: { personId: $personId }) {
      id
      deletedAt
      role
    }
  }
`;

export interface GrantInput {
  resource: string;
  level: PermissionLevel;
  condition: GrantCondition;
}

export const CREATE_ROLE = gql`
  mutation CreateRole($tenantId: UUID!, $name: String!, $grants: [RoleGrantInput!]) {
    createRole(tenantId: $tenantId, name: $name, grants: $grants) {
      ${ROLE_FIELDS}
    }
  }
`;

export const RENAME_ROLE = gql`
  mutation RenameRole($roleId: UUID!, $name: String!) {
    renameRole(roleId: $roleId, name: $name) {
      ${ROLE_FIELDS}
    }
  }
`;

export const SET_ROLE_GRANTS = gql`
  mutation SetRoleGrants($roleId: UUID!, $grants: [RoleGrantInput!]!) {
    setRoleGrants(roleId: $roleId, grants: $grants) {
      ${ROLE_FIELDS}
    }
  }
`;

export const DELETE_ROLE = gql`
  mutation DeleteRole($roleId: UUID!) {
    deleteRole(roleId: $roleId)
  }
`;

export const ASSIGN_ROLE = gql`
  mutation AssignRole($roleId: UUID!, $personId: UUID!) {
    assignRole(roleId: $roleId, personId: $personId) {
      ${ROLE_FIELDS}
    }
  }
`;

export const UNASSIGN_ROLE = gql`
  mutation UnassignRole($roleId: UUID!, $personId: UUID!) {
    unassignRole(roleId: $roleId, personId: $personId) {
      ${ROLE_FIELDS}
    }
  }
`;

/** Admin side: every brokerage, and who manages each with which grants. */
export interface PortfolioManager {
  id: string;
  tenantId: string | null;
  personId: string | null;
  grants: { resource: string; level: PermissionLevel }[];
}

export const PORTFOLIOS_QUERY = gql`
  query Portfolios {
    brokerages {
      ${SUMMARY_FIELDS}
    }
    managedTenants {
      id
      managers {
        id
        tenantId
        personId
        grants { resource level }
      }
    }
  }
`;

export interface PortfoliosResult {
  brokerages: BrokerageSummary[];
  managedTenants: { id: string; managers: PortfolioManager[] }[];
}

const MANAGER_FIELDS = `
  id
  tenantId
  personId
  grants { resource level }
`;

export const ASSIGN_TENANT_MANAGER = gql`
  mutation AssignTenantManager($tenantId: UUID!, $personId: UUID!, $grants: [RoleGrantInput!]!) {
    assignTenantManager(tenantId: $tenantId, personId: $personId, grants: $grants) {
      ${MANAGER_FIELDS}
    }
  }
`;

export const UNASSIGN_TENANT_MANAGER = gql`
  mutation UnassignTenantManager($tenantId: UUID!, $personId: UUID!) {
    unassignTenantManager(tenantId: $tenantId, personId: $personId) {
      id
      deletedAt
    }
  }
`;
